import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  useAutomationRules,
  useCreateAutomationRule,
  useUpdateAutomationRule,
  useDeleteAutomationRule,
  useAutomationRuns,
  AutomationRuleRecord,
  AutomationTriggerEvent,
  AutomationActionType,
  AutomationPeriodType,
  TRIGGER_EVENT_LABEL,
  ACTION_TYPE_LABEL,
  PERIOD_TYPE_LABEL,
  PROCEDURE_STATUS_OPTIONS,
} from "@/api/automations";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

interface RuleFormValues {
  name: string;
  description?: string;
  triggerEvent: AutomationTriggerEvent;
  triggerStatus?: string;
  actionType: AutomationActionType;
  actionTitle?: string;
  actionDaysOffset?: number;
  actionAssignToSourceAssignee?: boolean;
  actionPeriodType?: AutomationPeriodType;
  isActive: boolean;
}

function buildActionConfig(values: RuleFormValues): Record<string, unknown> {
  if (values.actionType === "CREATE_FOLLOWUP_TASK") {
    return {
      title: values.actionTitle,
      daysOffset: values.actionDaysOffset ? Number(values.actionDaysOffset) : undefined,
      assignToSourceAssignee: !!values.actionAssignToSourceAssignee,
    };
  }
  return { periodType: values.actionPeriodType };
}

function actionConfigSummary(rule: AutomationRuleRecord): string {
  if (rule.actionType === "CREATE_FOLLOWUP_TASK") {
    const title = (rule.actionConfig.title as string) ?? "";
    const days = (rule.actionConfig.daysOffset as number) ?? 3;
    return `"${title}" (vence en ${days} días)`;
  }
  const periodType = rule.actionConfig.periodType as AutomationPeriodType;
  return PERIOD_TYPE_LABEL[periodType] ?? "";
}

export default function AutomationRulesPage() {
  const { data: rules, isLoading } = useAutomationRules();
  const [showCreate, setShowCreate] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRuleRecord | null>(null);
  const [viewingRunsFor, setViewingRunsFor] = useState<AutomationRuleRecord | null>(null);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Automatizaciones</h1>
          <p className="mt-1 text-sm text-gray-500">
            Reglas "cuando ocurre X, hacer Y" sobre tareas, trámites, impuestos y facturas.
          </p>
        </div>
        {hasPermission("automations.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            + Nueva regla
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Regla</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Disparador</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Acción</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && (rules ?? []).length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  No hay reglas de automatización configuradas todavía.
                </td>
              </tr>
            )}
            {(rules ?? []).map((rule) => (
              <tr key={rule.id}>
                <td className="px-4 py-3 font-medium text-gray-900">
                  {rule.name}
                  {rule.description && <p className="text-xs font-normal text-gray-400">{rule.description}</p>}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {TRIGGER_EVENT_LABEL[rule.triggerEvent]}
                  {rule.triggerStatus && <span className="text-gray-400"> → {rule.triggerStatus}</span>}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {ACTION_TYPE_LABEL[rule.actionType]}
                  <p className="text-xs text-gray-400">{actionConfigSummary(rule)}</p>
                </td>
                <td className="px-4 py-3">
                  {rule.isActive ? (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">Activa</span>
                  ) : (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Inactiva</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  <button
                    type="button"
                    className="text-brand-600 hover:text-brand-800 text-xs font-medium"
                    onClick={() => setViewingRunsFor(rule)}
                  >
                    Ver ejecuciones
                  </button>
                  {hasPermission("automations.update") && (
                    <button
                      type="button"
                      className="text-brand-600 hover:text-brand-800 text-xs font-medium"
                      onClick={() => setEditingRule(rule)}
                    >
                      Editar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <RuleModal mode="create" onClose={() => setShowCreate(false)} />}
      {editingRule && <RuleModal mode="edit" rule={editingRule} onClose={() => setEditingRule(null)} />}
      {viewingRunsFor && <RunsModal rule={viewingRunsFor} onClose={() => setViewingRunsFor(null)} />}
    </div>
  );
}

function RuleModal({ mode, rule, onClose }: { mode: "create" | "edit"; rule?: AutomationRuleRecord; onClose: () => void }) {
  const createRule = useCreateAutomationRule();
  const updateRule = useUpdateAutomationRule();
  const deleteRule = useDeleteAutomationRule();
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { register, handleSubmit, watch } = useForm<RuleFormValues>({
    defaultValues: rule
      ? {
          name: rule.name,
          description: rule.description ?? undefined,
          triggerEvent: rule.triggerEvent,
          triggerStatus: rule.triggerStatus ?? undefined,
          actionType: rule.actionType,
          actionTitle: rule.actionType === "CREATE_FOLLOWUP_TASK" ? (rule.actionConfig.title as string) : undefined,
          actionDaysOffset: rule.actionType === "CREATE_FOLLOWUP_TASK" ? (rule.actionConfig.daysOffset as number) : undefined,
          actionAssignToSourceAssignee:
            rule.actionType === "CREATE_FOLLOWUP_TASK" ? !!rule.actionConfig.assignToSourceAssignee : undefined,
          actionPeriodType: rule.actionType === "CREATE_NEXT_TAX_OBLIGATION" ? (rule.actionConfig.periodType as AutomationPeriodType) : undefined,
          isActive: rule.isActive,
        }
      : { triggerEvent: "TASK_COMPLETED", actionType: "CREATE_FOLLOWUP_TASK", isActive: true },
  });

  const triggerEvent = watch("triggerEvent");
  const actionType = watch("actionType");

  const onSubmit = async (values: RuleFormValues) => {
    const payload = {
      name: values.name,
      description: values.description || undefined,
      triggerEvent: values.triggerEvent,
      triggerStatus: values.triggerEvent === "PROCEDURE_STATUS_CHANGED" ? values.triggerStatus : undefined,
      actionType: values.actionType,
      actionConfig: buildActionConfig(values),
      isActive: values.isActive,
    };
    try {
      if (mode === "create") {
        await createRule.mutateAsync(payload);
        toast.success("Regla de automatización creada correctamente.");
      } else if (rule) {
        await updateRule.mutateAsync({ id: rule.id, ...payload });
        toast.success("Regla de automatización actualizada correctamente.");
      }
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!rule) return;
    if (!window.confirm(`¿Eliminar la regla "${rule.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteRule.mutateAsync(rule.id);
      toast.success("Regla eliminada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const saving = createRule.isPending || updateRule.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900">{mode === "create" ? "Nueva regla de automatización" : `Editar regla — ${rule?.name}`}</h2>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" placeholder="Siguiente IVA mensual" {...register("name", { required: true })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Descripción (opcional)</label>
            <input className="input-field mt-1" {...register("description")} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cuando ocurre...</label>
            <select className="input-field mt-1" {...register("triggerEvent", { required: true })}>
              {(Object.keys(TRIGGER_EVENT_LABEL) as AutomationTriggerEvent[]).map((event) => (
                <option key={event} value={event}>
                  {TRIGGER_EVENT_LABEL[event]}
                </option>
              ))}
            </select>
          </div>

          {triggerEvent === "PROCEDURE_STATUS_CHANGED" && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Estado destino del trámite</label>
              <select className="input-field mt-1" {...register("triggerStatus", { required: true })}>
                {PROCEDURE_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Ejecutar...</label>
            <select className="input-field mt-1" {...register("actionType", { required: true })}>
              {(Object.keys(ACTION_TYPE_LABEL) as AutomationActionType[])
                .filter((type) => type !== "CREATE_NEXT_TAX_OBLIGATION" || triggerEvent === "TAX_OBLIGATION_PAID")
                .map((type) => (
                  <option key={type} value={type}>
                    {ACTION_TYPE_LABEL[type]}
                  </option>
                ))}
            </select>
          </div>

          {actionType === "CREATE_FOLLOWUP_TASK" && (
            <div className="space-y-3 rounded-md border border-gray-200 p-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Título de la tarea de seguimiento</label>
                <input className="input-field mt-1" placeholder="Revisar documentación" {...register("actionTitle", { required: true })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Días de plazo desde la fecha de disparo</label>
                <input type="number" min={1} className="input-field mt-1" defaultValue={3} {...register("actionDaysOffset", { valueAsNumber: true })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" {...register("actionAssignToSourceAssignee")} />
                Asignar al mismo responsable de la tarea/trámite/impuesto/factura de origen
              </label>
            </div>
          )}

          {actionType === "CREATE_NEXT_TAX_OBLIGATION" && (
            <div className="rounded-md border border-gray-200 p-3">
              <label className="block text-sm font-medium text-gray-700">Periodicidad de la nueva obligación</label>
              <select className="input-field mt-1" {...register("actionPeriodType", { required: true })}>
                {(Object.keys(PERIOD_TYPE_LABEL) as AutomationPeriodType[]).map((period) => (
                  <option key={period} value={period}>
                    {PERIOD_TYPE_LABEL[period]}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" defaultChecked {...register("isActive")} />
            Regla activa
          </label>

          <div className="flex items-center justify-between gap-2 pt-2">
            {mode === "edit" && hasPermission("automations.delete") ? (
              <button type="button" className="text-red-600 hover:text-red-800 text-sm font-medium" onClick={handleDelete}>
                Eliminar regla
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Guardando..." : mode === "create" ? "Crear regla" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function RunsModal({ rule, onClose }: { rule: AutomationRuleRecord; onClose: () => void }) {
  const { data: runs, isLoading } = useAutomationRuns(rule.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900">Ejecuciones — {rule.name}</h2>
        <div className="mt-4 space-y-2">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {!isLoading && (runs ?? []).length === 0 && (
            <p className="text-sm text-gray-400">Esta regla todavía no se ha ejecutado.</p>
          )}
          {(runs ?? []).map((run) => (
            <div key={run.id} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
              <div>
                <p className="text-gray-900">
                  {run.sourceEntityType} → {run.resultEntityType ?? "—"}
                </p>
                <p className="text-xs text-gray-400">{new Date(run.createdAt).toLocaleString("es-CO")}</p>
                {run.errorMessage && <p className="text-xs text-red-600">{run.errorMessage}</p>}
              </div>
              {run.status === "SUCCESS" ? (
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">Exitosa</span>
              ) : (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">Fallida</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-4">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
